import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'

// A scroll-snap track of full-width slides: arrows, dots and arrow keys scroll it by one
// slide, scrolling by hand (or swiping) moves the active dot. While a smooth scroll is
// under way the slide it heads for counts as current, so repeated clicks keep advancing.
// Each slide carries data-current, and motion.css sets the ones away from the centre back a little.
export default class PhotoGallery extends Controller {
  static targets = ['track', 'slide', 'dot', 'previous', 'next']

  connect () {
    this.target = null
    this.sync()
  }

  previous (event) {
    if (this.#inLightbox(event)) return

    event.preventDefault()
    this.#quiet(event)
    this.#scrollTo(this.#current - 1)
  }

  next (event) {
    if (this.#inLightbox(event)) return

    event.preventDefault()
    this.#quiet(event)
    this.#scrollTo(this.#current + 1)
  }

  #quiet (event) {
    if (event?.type === 'keydown') this.element.focus({ preventScroll: true })
  }

  // The viewer is a dialog inside this element, so its keystrokes bubble to the track's own
  // arrow-key actions. They belong to whichever photograph is being looked at, not to the track
  // behind it: the lightbox answers them itself.
  #inLightbox (event) {
    return Boolean(event.target?.closest?.('dialog[open]'))
  }

  go ({ params: { index } }) {
    this.#scrollTo(index)
  }

  sync () {
    this.index = Math.round(this.trackTarget.scrollLeft / (this.trackTarget.clientWidth || 1))
    if (this.index === this.target) this.target = null
    this.#render()
  }

  settle () {
    this.target = null
    this.sync()
  }

  get #current () {
    return this.target ?? this.index
  }

  #scrollTo (index) {
    const clamped = Math.max(0, Math.min(index, this.slideTargets.length - 1))
    if (clamped === this.#current) return

    this.target = clamped
    this.#render()
    this.trackTarget.scrollTo({ left: clamped * this.trackTarget.clientWidth, behavior: 'smooth' })
  }

  // aria-disabled rather than disabled, so a focused arrow keeps focus at the ends.
  #render () {
    const current = this.#current
    this.dotTargets.forEach((dot, index) => dot.setAttribute('aria-current', index === current))
    this.slideTargets.forEach((slide, index) => { slide.dataset.current = index === current })
    const first = current === 0
    const last = current >= this.slideTargets.length - 1
    this.previousTargets.forEach((arrow) => arrow.setAttribute('aria-disabled', first))
    this.nextTargets.forEach((arrow) => arrow.setAttribute('aria-disabled', last))

    // Only the photograph on show can be tabbed to. The others sit off to the side of a scrolling
    // track, and focusing one makes the browser scroll it into view — which the track reads as the
    // reader moving to that slide, so Tab would page the gallery instead of walking its controls.
    this.slideTargets.forEach((slide, index) => {
      slide.querySelector('.photo_gallery__open')?.setAttribute('tabindex', index === current ? '0' : '-1')
    })
  }
}

stimulus.register('photo-gallery', PhotoGallery)
